import { useEffect, useState, useCallback } from "react";
import { Car, ShieldCheck, Bus } from "lucide-react";
import api from "@/lib/api";

const BG_STYLE = {
  background:
    "radial-gradient(1200px 600px at 15% 10%, rgba(37,99,235,0.18), transparent 60%)," +
    "radial-gradient(900px 500px at 95% 90%, rgba(14,165,233,0.18), transparent 55%)," +
    "linear-gradient(135deg, #f8fafc 0%, #eef2ff 55%, #e0f2fe 100%)",
};

const PAGE_SIZE = 50;
const ROTATE_MS = 8000;

export default function DismissalBoardPage() {
  const [queue, setQueue] = useState([]);
  const [busCalls, setBusCalls] = useState([]);
  const [clock, setClock] = useState(new Date());
  const [building, setBuilding] = useState({ name: "", logo: "" });
  const [page, setPage] = useState(0);

  useEffect(() => {
    api.get("/settings/building").then(({ data }) => {
      setBuilding({ name: data.name || "", logo: data.effective_logo_data_url || data.logo_data_url || "" });
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      const [q, b] = await Promise.all([api.get("/dismissal/queue"), api.get("/dismissal/buses")]);
      setQueue(q.data);
      setBusCalls(b.data);
    } catch { /* polling */ }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 3000);
    const ck = setInterval(() => setClock(new Date()), 1000);
    return () => { clearInterval(iv); clearInterval(ck); };
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(queue.length / PAGE_SIZE));

  useEffect(() => {
    if (totalPages <= 1) { setPage(0); return; }
    const rot = setInterval(() => setPage((p) => (p + 1) % totalPages), ROTATE_MS);
    return () => clearInterval(rot);
  }, [totalPages]);

  const safePage = Math.min(page, totalPages - 1);
  const visibleQueue = queue.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  return (
    <div className="relative min-h-screen overflow-hidden flex flex-col" style={BG_STYLE} data-testid="dismissal-board">
      <header className="flex items-center justify-between px-12 py-8">
        <div className="flex items-center gap-3 rounded-full bg-white/80 px-6 py-3 backdrop-blur-xl border border-white/60 shadow-sm" data-testid="board-school-brand">
          {building.logo ? (
            <img src={building.logo} alt={building.name} className="h-12 w-12 rounded-full object-contain" data-testid="board-school-logo" />
          ) : (
            <Car className="h-7 w-7 text-primary" />
          )}
          <span className="font-display text-3xl font-bold tracking-tight text-slate-900" data-testid="board-school-name">
            {building.name || "Car Rider Pickup"}
          </span>
        </div>
        <div className="rounded-full bg-white/80 px-6 py-3 backdrop-blur-xl border border-white/60 shadow-sm font-display text-3xl font-semibold tracking-tight text-slate-700" data-testid="board-clock">
          {clock.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
        </div>
      </header>

      <main className="flex-1 px-12 pb-8">
        {busCalls.length > 0 && (
          <div className="mb-6 flex flex-wrap gap-4" data-testid="board-bus-list">
            {busCalls.map((b) => (
              <div key={b.id} data-testid={`board-bus-${b.id}`}
                className="kiosk-rise flex items-center gap-4 rounded-3xl bg-amber-400 px-7 py-5 shadow-[0_25px_60px_-15px_rgba(245,158,11,0.55)]">
                <Bus className="h-10 w-10 text-slate-900" />
                <div>
                  <div className="font-display text-4xl font-bold tracking-tight text-slate-900">BUS {b.bus_number}</div>
                  <div className="text-lg font-semibold text-slate-800/80 uppercase tracking-widest">Now boarding</div>
                </div>
              </div>
            ))}
          </div>
        )}
        {queue.length === 0 && busCalls.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center" data-testid="board-empty">
            <div className="kiosk-rise rounded-3xl border border-white/60 bg-white/85 backdrop-blur-2xl px-16 py-14 text-center shadow-[0_25px_60px_-15px_rgba(15,23,42,0.25)]">
              <Car className="h-16 w-16 mx-auto mb-6 text-primary/30" />
              <div className="font-display text-4xl font-bold tracking-tight text-slate-900">Waiting for calls…</div>
              <div className="mt-3 text-xl text-slate-500">Student names will appear here at dismissal.</div>
            </div>
          </div>
        ) : (
          <div className="grid gap-2 md:grid-cols-3 xl:grid-cols-5" data-testid="board-list" key={safePage}>
            {visibleQueue.map((c) => {
              const latest = queue.length > 0 && c.id === queue[queue.length - 1].id;
              return (
                <div
                  key={c.id}
                  data-testid={`board-call-${c.id}`}
                  className={`kiosk-rise flex items-center gap-2.5 rounded-xl px-3 py-1.5 backdrop-blur-2xl transition ${
                    latest
                      ? "bg-primary text-white shadow-[0_15px_40px_-12px_rgba(37,99,235,0.55)]"
                      : "border border-white/60 bg-white/85 shadow-[0_10px_30px_-12px_rgba(15,23,42,0.18)]"
                  }`}
                >
                  <div className={`shrink-0 h-9 w-12 rounded-lg flex items-center justify-center font-display text-xl font-bold tracking-tight ${
                    latest ? "bg-white/20 text-white" : "bg-primary/10 text-primary"
                  }`}>
                    {c.car_number}
                  </div>
                  <div className="min-w-0 flex items-baseline gap-1.5">
                    <span className={`font-display text-lg xl:text-xl font-bold tracking-tight truncate ${latest ? "text-white" : "text-slate-900"}`}>
                      {c.student_name}
                    </span>
                    {c.grade && <span className={`shrink-0 text-sm ${latest ? "text-white/80" : "text-slate-500"}`}>{c.grade}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-center gap-3" data-testid="board-pagination">
            <div className="flex items-center gap-2">
              {Array.from({ length: totalPages }).map((_, i) => (
                <span
                  key={i}
                  data-testid={`board-page-dot-${i}`}
                  className={`rounded-full transition-all duration-300 ${
                    i === safePage ? "h-3.5 w-8 bg-primary" : "h-3.5 w-3.5 bg-slate-300"
                  }`}
                />
              ))}
            </div>
            <span className="text-lg font-semibold text-slate-500" data-testid="board-page-label">
              {safePage * PAGE_SIZE + 1}–{Math.min(queue.length, (safePage + 1) * PAGE_SIZE)} of {queue.length}
            </span>
          </div>
        )}
      </main>

      <footer className="px-12 py-5 flex items-center justify-between text-sm text-slate-500">
        <span>Please pull forward when your student's name appears.</span>
        <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-primary" /> Logerd dismissal board</span>
      </footer>
    </div>
  );
}
